import { SetStateAction, Dispatch, useState } from "react";
import { useSelector } from "react-redux";
import { Dialog, Typography, IconButton, Button, Select } from "@material-tailwind/react";
import { Xmark } from "iconoir-react";

import AppImages from "../../../../assets/AppImages";
import FileUpload from "../../../../components/FileUpload";
import { RootState } from "../../../../redux";

interface DialogUploadDocProps {
    isOpen: boolean;
    setIsOpen: Dispatch<SetStateAction<boolean>>;
    onSubmit: (files: FileList, documentType: string) => void;
}

export const DialogUploadDoc: React.FC<DialogUploadDocProps> = ({ isOpen, setIsOpen, onSubmit }) => {
    const { constants } = useSelector((state: RootState) => state.auth);
    const [files, setFiles] = useState<FileList | null>(null);
    const [documentType, setDocumentType] = useState<string>("");
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <Dialog.Overlay>
                <Dialog.Content className="relative p-8">
                    <Typography variant="h4" color="primary" className="font-bold text-lg mb-4">
                        Upload Documents
                    </Typography>
                    <IconButton variant="ghost" className="absolute top-2 right-2" onClick={() => setIsOpen(false)}>
                        <Xmark className="w-5 h-5" />
                    </IconButton>
                    <div
                        className={`w-full h-[min(350px,50vh)] flex border-dashed border-2 p-10 items-center justify-center mb-4 ${
                            !files && isSubmitted ? "border-red-500" : "border-gray-400"
                        }`}
                    >
                        <FileUpload
                            name={`doc_uploaad`}
                            isMultiple={true}
                            accept=".jpg,.png,.jpeg,.pdf"
                            title=""
                            titleClass="hidden"
                            btnText="Choose Files"
                            Icon={AppImages.iconUploadFile}
                            iconBgClass="bg-transparent text-primary !p-0"
                            gap="gap-y-5"
                            desc="Drag & drop your documents here or"
                            media={null}
                            onFileChange={(files) => {
                                setFiles(files);
                            }}
                        />
                    </div>
                    {!files?.length && isSubmitted && (
                        <Typography type="small" className="text-red-500 mt-1">
                            Please select files
                        </Typography>
                    )}

                    <div className="flex my-5 flex-col gap-2">
                        <Typography type="small" className="block text-primary font-bold">
                            Select type of document
                        </Typography>
                        <Select value={documentType} onValueChange={(e) => setDocumentType(e)}>
                            <Select.Trigger
                                placeholder="Select Document Type"
                                className={`w-full p-2 border rounded ${
                                    !documentType && isSubmitted
                                        ? "border-red-500"
                                        : "border-gray-300"
                                }`}
                            />
                            <Select.List>
                                {constants?.["DOCUMENT_TYPES"].map((el, idx: number) => (
                                    <Select.Option key={idx} value={el[0]}>
                                        {el[1]}
                                    </Select.Option>
                                ))}
                            </Select.List>
                        </Select>
                        {!documentType && isSubmitted && (
                            <Typography type="small" className="text-red-500 mt-1">
                                Please select a document type
                            </Typography>
                        )}
                    </div>

                    {files?.length && (
                        <div className="flex my-5 flex-col gap-2">
                            <Typography type="small" className="block text-primary font-bold">
                                Selected Files
                            </Typography>
                            {Array.from(files).map((file, index) => (
                                <div
                                    key={index}
                                    className="flex justify-between items-center border p-2 rounded"
                                >
                                    <Typography className="text-sm text-gray-700">
                                        {file.name}
                                    </Typography>
                                    <IconButton
                                        variant="ghost"
                                        onClick={() => {
                                            const newFiles = Array.from(files);
                                            newFiles.splice(index, 1);
                                            setFiles(newFiles as unknown as FileList);
                                        }}
                                    >
                                        <AppImages.iconTrash className="w-4 h-4" />
                                    </IconButton>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="mb-1 flex items-center justify-end gap-2">
                        <Button color="secondary" onClick={() => setIsOpen(false)}>
                            Close
                        </Button>
                        <Button
                            color="primary"
                            onClick={() => {
                                setIsSubmitted(true);
                                if (files?.length && documentType) {
                                    onSubmit(files, documentType);
                                }
                            }}
                        >
                            Upload
                        </Button>
                    </div>
                </Dialog.Content>
            </Dialog.Overlay>
        </Dialog>
    );
};
